import type { FilterPreset } from '../data/filterCatalog';
import { createFilterNode } from '../data/filterCatalog';
import { NodeType, type StreamType, type WorkflowDocument, type WorkflowEdge, type WorkflowNode } from '../nodes/NodeTypes';
import { streamEdgeStyle } from './streamStyles';

export function edgeStream(edge: WorkflowEdge, nodes: WorkflowNode[]): StreamType | undefined {
  if (edge.data?.streamType) return edge.data.streamType;
  if (edge.sourceHandle === 'audio' || edge.targetHandle === 'audio') return 'audio';
  const source = nodes.find((node) => node.id === edge.source);
  const target = nodes.find((node) => node.id === edge.target);
  if (source?.type === NodeType.PROCESSING && source.data.streamType) return source.data.streamType;
  if (target?.type === NodeType.PROCESSING && target.data.streamType) return target.data.streamType;
  return 'video';
}

function makeEdge(source: WorkflowNode, target: WorkflowNode, streamType: StreamType, id: string): WorkflowEdge {
  return {
    id,
    source: source.id,
    target: target.id,
    sourceHandle: source.type === NodeType.INPUT ? streamType : 'output',
    targetHandle: target.type === NodeType.OUTPUT ? streamType : 'input',
    data: { streamType },
    type: 'smoothstep',
    style: streamEdgeStyle(streamType),
  };
}

export function insertPreset(
  workflow: WorkflowDocument,
  preset: FilterPreset,
  id: string,
): { workflow: WorkflowDocument; nodeId: string } {
  const position = { x: 420, y: 430 + workflow.nodes.length * 26 };
  const node = createFilterNode(preset, id, position);
  if (preset.operation === 'option' || !preset.streamType) {
    return { workflow: { ...workflow, nodes: [...workflow.nodes, node] }, nodeId: id };
  }

  const output = workflow.nodes.find((item) => item.type === NodeType.OUTPUT);
  const route = output && workflow.edges.find((edge) => edge.target === output.id && edgeStream(edge, workflow.nodes) === preset.streamType);
  const source = route ? workflow.nodes.find((item) => item.id === route.source) : workflow.nodes.find((item) => item.type === NodeType.INPUT);
  if (!source) return { workflow: { ...workflow, nodes: [...workflow.nodes, node] }, nodeId: id };

  const sourcePosition = source.position;
  const targetPosition = output?.position || { x: source.position.x + 520, y: source.position.y };
  node.position = {
    x: Math.round((sourcePosition.x + targetPosition.x) / 2),
    y: preset.streamType === 'audio' ? targetPosition.y + 140 : targetPosition.y,
  };

  const keptEdges = route ? workflow.edges.filter((edge) => edge.id !== route.id) : workflow.edges;
  const nextEdges = [
    makeEdge(source, node, preset.streamType, `e-${source.id}-${id}`),
    ...(output ? [makeEdge(node, output, preset.streamType, `e-${id}-${output.id}`)] : []),
  ];

  return {
    workflow: { ...workflow, nodes: [...workflow.nodes, node], edges: [...keptEdges, ...nextEdges] },
    nodeId: id,
  };
}

export function orderedStreamFilters(workflow: WorkflowDocument, streamType: StreamType): WorkflowNode[] {
  const input = workflow.nodes.find((node) => node.type === NodeType.INPUT);
  if (!input) return [];
  const result: WorkflowNode[] = [];
  const visited = new Set<string>([input.id]);
  let currentId = input.id;

  for (let step = 0; step < workflow.nodes.length; step += 1) {
    const nextEdge = workflow.edges
      .filter((edge) => edge.source === currentId && edgeStream(edge, workflow.nodes) === streamType)
      .sort((a, b) => {
        const nodeA = workflow.nodes.find((node) => node.id === a.target);
        const nodeB = workflow.nodes.find((node) => node.id === b.target);
        return (nodeA?.position.x || 0) - (nodeB?.position.x || 0);
      })[0];
    if (!nextEdge || visited.has(nextEdge.target)) break;
    visited.add(nextEdge.target);
    const nextNode = workflow.nodes.find((node) => node.id === nextEdge.target);
    if (!nextNode || nextNode.type === NodeType.OUTPUT) break;
    if (nextNode.type === NodeType.PROCESSING && nextNode.data.operation !== 'option') result.push(nextNode);
    currentId = nextNode.id;
  }
  return result;
}

export function reorderStreamFilter(workflow: WorkflowDocument, streamType: StreamType, nodeId: string, direction: -1 | 1): WorkflowDocument {
  const ordered = orderedStreamFilters(workflow, streamType);
  const currentIndex = ordered.findIndex((node) => node.id === nodeId);
  const nextIndex = currentIndex + direction;
  if (currentIndex < 0 || nextIndex < 0 || nextIndex >= ordered.length) return workflow;
  const reordered = [...ordered];
  [reordered[currentIndex], reordered[nextIndex]] = [reordered[nextIndex], reordered[currentIndex]];
  const input = workflow.nodes.find((node) => node.type === NodeType.INPUT);
  const output = workflow.nodes.find((node) => node.type === NodeType.OUTPUT);
  if (!input || !output) return workflow;
  const route = [input, ...reordered, output];
  const routeIds = new Set(route.map((node) => node.id));
  const keptEdges = workflow.edges.filter((edge) => !(edgeStream(edge, workflow.nodes) === streamType && routeIds.has(edge.source) && routeIds.has(edge.target)));
  const routeEdges = route.slice(0, -1).map((source, index) => {
    const target = route[index + 1];
    return makeEdge(source, target, streamType, `e-${source.id}-${streamType}-${target.id}`);
  });
  return {
    ...workflow,
    nodes: workflow.nodes.map((node) => {
      const position = reordered.findIndex((item) => item.id === node.id);
      return position >= 0 ? { ...node, position: { x: 360 + position * 245, y: streamType === 'audio' ? 340 : 200 } } : node;
    }),
    edges: [...keptEdges, ...routeEdges],
  };
}

export function layoutWorkflow(nodes: WorkflowNode[], edges: WorkflowEdge[]): WorkflowNode[] {
  const pending = new Set(nodes.map((node) => node.id));
  const ordered: WorkflowNode[] = [];
  const levelById = new Map<string, number>();

  while (pending.size > 0) {
    const ready = nodes.filter((node) => pending.has(node.id) && edges
      .filter((edge) => edge.target === node.id)
      .every((edge) => !pending.has(edge.source)));
    if (ready.length === 0) break;
    ready.forEach((node) => {
      const parentLevels = edges.filter((edge) => edge.target === node.id).map((edge) => levelById.get(edge.source) || 0);
      const level = parentLevels.length ? Math.max(...parentLevels) + 1 : 0;
      levelById.set(node.id, level);
      ordered.push(node);
      pending.delete(node.id);
    });
  }

  nodes.filter((node) => pending.has(node.id)).forEach((node) => ordered.push(node));
  const rowByLevel = new Map<number, number>();
  return ordered.map((node) => {
    const level = levelById.get(node.id) || 0;
    const row = rowByLevel.get(level) || 0;
    rowByLevel.set(level, row + 1);
    const streamOffset = node.data.streamType === 'audio' ? 150 : 0;
    return { ...node, position: { x: 120 + level * 250, y: 190 + streamOffset + row * 78 } };
  });
}

export function nextNodeId(nodes: WorkflowNode[], type: 'input' | 'filter' | 'output'): string {
  const used = new Set(nodes.map((node) => node.id));
  let index = 1;
  let candidate = `${type}-${index}`;
  while (used.has(candidate)) {
    index += 1;
    candidate = `${type}-${index}`;
  }
  return candidate;
}

export function createInputNode(id: string, position: { x: number; y: number }, filename = ''): WorkflowNode {
  return {
    id,
    type: NodeType.INPUT,
    deletable: true,
    position,
    data: { label: '输入文件', params: { filename, seek: '', duration: '' } },
  };
}

export function removeWorkflowNode(workflow: WorkflowDocument, nodeId: string): WorkflowDocument {
  if (!workflow.nodes.some((node) => node.id === nodeId)) return workflow;
  return {
    ...workflow,
    nodes: workflow.nodes.filter((node) => node.id !== nodeId),
    edges: workflow.edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
  };
}

export function connectInputToOutput(input: WorkflowNode, output: WorkflowNode): WorkflowEdge[] {
  return [
    makeEdge(input, output, 'video', `e-${input.id}-video-${output.id}`),
    makeEdge(input, output, 'audio', `e-${input.id}-audio-${output.id}`),
  ];
}

export function duplicateWorkflowNode(workflow: WorkflowDocument, nodeId: string, newId: string): WorkflowDocument | null {
  const source = workflow.nodes.find((node) => node.id === nodeId);
  if (!source || source.type === NodeType.OUTPUT) return null;

  const clone: WorkflowNode = {
    ...source,
    id: newId,
    position: { x: source.position.x + 44, y: source.position.y + 44 },
    selected: false,
    dragging: false,
    data: { ...source.data, params: source.data.params ? { ...source.data.params } : undefined },
  };
  const output = workflow.nodes.find((node) => node.type === NodeType.OUTPUT);
  const copiedEdges = source.type === NodeType.INPUT && output
    ? connectInputToOutput(clone, output)
    : workflow.edges
      .filter((edge) => edge.source === nodeId || edge.target === nodeId)
      .map((edge, index) => {
      const streamType = edgeStream(edge, workflow.nodes) || 'video';
      return {
        ...edge,
        id: `e-${newId}-${index + 1}`,
        source: edge.source === nodeId ? newId : edge.source,
        target: edge.target === nodeId ? newId : edge.target,
        style: streamEdgeStyle(streamType),
        data: { ...edge.data, streamType },
      };
      });

  return { ...workflow, nodes: [...workflow.nodes, clone], edges: [...workflow.edges, ...copiedEdges] };
}

export function isStreamConnectionValid(
  source: WorkflowNode | undefined,
  target: WorkflowNode | undefined,
  sourceHandle?: string | null,
  targetHandle?: string | null,
): boolean {
  if (!source || !target || source.id === target.id) return false;
  if (source.type === NodeType.OUTPUT || target.type === NodeType.INPUT) return false;
  if (source.type === NodeType.PROCESSING && source.data.operation === 'option') return false;
  const sourceStream = source.type === NodeType.INPUT
    ? (sourceHandle === 'audio' ? 'audio' : 'video')
    : source.data.streamType || (source.data.isAudio ? 'audio' : 'video');
  if (target.type === NodeType.OUTPUT) return !targetHandle || targetHandle === sourceStream;
  if (target.data.operation === 'option') return false;
  const targetStream = target.data.streamType || (target.data.isAudio ? 'audio' : 'video');
  return sourceStream === targetStream;
}
